import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../../stores/authStore';
import { getPreviousRegistrationRoute, REGISTRATION_FLOW_2_0 } from '@constants/userFlow';
import {
  getCurrentPlanFromPaymentStatus,
  effectivePlanStatus,
} from '../../../lib/services/payment/planUtils';

/**
 * Back handler for the Onboarding 2.0 step pages. `advance` navigates forward
 * with `replace: true`, so browser history has no previous-step entry — a plain
 * `navigate(-1)` would jump OUT of the flow. Instead we resolve the previous
 * APPLICABLE step from the flow (skipping the steps this user skips) and go
 * there. The guard allows revisiting earlier completed steps. Falls back to
 * `navigate(-1)` on the first step / outside the flow (e.g. the dev preview).
 */
export function useOnboardingBack() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { logout } = useAuthStore();

  return () => {
    const user = useAuthStore.getState().user;
    const hasShopify = user?.installations?.some(i => i.platform === 'shopify') ?? false;
    const treatPaymentDone =
      getCurrentPlanFromPaymentStatus(effectivePlanStatus(user) ?? '') !== null ||
      !!user?.inFreeTrial;
    const prev = getPreviousRegistrationRoute(pathname, hasShopify, treatPaymentDone);
    if (prev) {
      navigate(prev);
      return;
    }
    // WOR-1384 (TC-2-07): no previous FLOW step means we're on the FIRST onboarding step (Business,
    // Step 2 of the signup). `navigate(-1)` was the original bug — with no in-app history (advance()
    // replaces) it landed on about:blank, and with organic history it did nothing.
    //
    // We also can't just `navigate('/auth/register')`: by now the account is created and the user is
    // logged in, so AuthLayout redirects an authed user straight back to '/' — i.e. no response. To
    // genuinely return to Step 1 (the account screen), LOG OUT first, then go to /auth/register. The
    // email is now registered, so re-submitting it surfaces the WOR-1381 "already registered → Sign
    // in" guidance rather than a dead end. Only fall back to browser Back when we are genuinely
    // outside the registration flow (e.g. the dev preview route).
    const inRegistrationFlow = REGISTRATION_FLOW_2_0.some(s => pathname.startsWith(s.route));
    if (inRegistrationFlow) {
      void Promise.resolve(logout()).finally(() => navigate('/auth/register', { replace: true }));
    } else {
      navigate(-1);
    }
  };
}
